<template>
    <div class="p-4">
        <ValidationObserver ref="programForm">
            <fieldset class="border p-3 mb-4 rounded bg-white shadow-sm">
                <legend class="w-auto px-2 font-weight-bold h6">{{ $t('education_information')}}</legend>
                <div class="mb-4">
                    <ValidationProvider name="document_barcode_no" ref="yoksisProvider"  v-slot="{ errors ,valid}" rules ="length:18">
                        <b-col cols="12" md="6">
                                <b-form-group>
                                    <label class="mb-2 text-black-75" v-b-tooltip.hover.bottom :title="$t('document_barcode_no_info')">
                                        {{$t('document_barcode_no')}}
                                        <span class="text-primary"><i class="ri-information-2-fill"></i></span>
                                    </label>
                                    <b-form-input
                                        v-model="formData.document_barcode_no"
                                        :state="errors[0] ? false : null" />
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" />
                                </b-form-group>
                        </b-col>
                            <b-col cols="12" md="6">
                                <b-button variant="primary" @click="fetchYoksisData" class="font-weight-bold" :disabled="(!valid || formData.document_barcode_no == null)  || fetchingYoksis">
                                    <b-spinner small v-if="fetchingYoksis"></b-spinner>
                                    <i v-else class="ri-download-cloud-2-line mr-1"></i> {{ $t('search_by_yoksis') }}
                                </b-button>
                            </b-col>
                    </ValidationProvider>
                </div>
                <div v-show="yoksisResults.length > 0" class="table-responsive mb-4">
                    <b-table hover bordered small head-variant="light" :items="yoksisResults" :fields="yoksisFields" class="font-size-13 text-center">
                        <template #cell(actions)="data">
                            <b-button size="sm" :variant="selectedEducation === data.item ? 'outline-success' : 'primary'" @click="selectEducation(data.item)">
                                <i :class="selectedEducation === data.item ? 'ri-check-line' : 'ri-add-line'"></i>
                                {{ selectedEducation === data.item ? $t('selected') : $t('select') }}
                            </b-button>
                        </template>
                    </b-table>
                </div>
                <div class="mb-3">
                        <b-form-checkbox
                            id="manualEduCheck"
                            v-model="formData.education_not_listed"
                            :value="true"
                            :unchecked-value="false"
                            @click="formData.education_not_listed = !formData.education_not_listed"
                        >
                            {{$t('add_unlisted_education')}}
                        </b-form-checkbox>
                </div>
                <b-collapse v-model="formData.education_not_listed">
                    <div class="p-3 bg-light rounded border">
                        <b-row>
                            <b-col sm="12" md="6" class="mb-3">
                                <ValidationProvider name="university" :rules="formData.education_not_listed ? 'required' : ''" v-slot="{valid, errors}">
                                    <b-form-group :label="$t('university_of_registration')">
                                        <b-form-input size="sm" v-model="formData.home_university_name" :state="errors[0] ? false : (valid ? true : null)"></b-form-input>
                                        <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>
                            <b-col sm="12" md="6" class="mb-3">
                                <ValidationProvider name="faculty" :rules="formData.education_not_listed ? 'required' : ''" v-slot="{valid, errors}">
                                    <b-form-group :label="$t('faculty_of_registration')">
                                        <b-form-input size="sm" v-model="formData.home_faculty_name" :state="errors[0] ? false : (valid ? true : null)"></b-form-input>
                                        <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <b-col sm="12" md="6" class="mb-3">
                                <ValidationProvider name="programLevel" :rules="formData.education_not_listed ? 'required' : ''" v-slot="{errors}">
                                    <b-form-group :label="$t('program_level')">
                                        <parameter-selectbox v-model="formData.home_program_level"
                                                             :public="true"
                                                             :validate-error="errors[0]" code="program_levels"></parameter-selectbox>
                                        <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>
                            <b-col sm="12" md="6" class="mb-3">
                                <ValidationProvider name="programName" :rules="formData.education_not_listed ? 'required' : ''" v-slot="{valid, errors}">
                                    <b-form-group :label="$t('program_of_registration')">
                                        <b-form-input size="sm" v-model="formData.home_program_name" :state="errors[0] ? false : (valid ? true : null)"></b-form-input>
                                        <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>
                            <b-col sm="12" md="6" class="mb-3">
                                <ValidationProvider name="gradeClass" rules="" v-slot="{valid, errors}">
                                    <b-form-group :label="$t('class')">
                                        <ParameterSelectbox code="classes" :public="true" v-model="formData.education_class" :state="errors[0] ? false : (valid ? true : null)" :validate-error="errors[0]"/>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>
                            <b-col cols="12" class="mt-2 text-right">
                                <b-button variant="primary" size="sm" @click="addManualEducation" class="font-weight-bold px-4">
                                    <i class="ri-add-line"></i> {{ $t('add') }}
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>
                </b-collapse>
            </fieldset>

            <fieldset class="border p-3 mb-4 rounded bg-white shadow-sm">
                <legend class="w-auto px-2 font-weight-bold h6">{{ $t('applied_program_info') }}</legend>
                <b-row class="align-items-start">
                    <template>
                        <b-col cols="12" class="mt-n2" key="ug-level">
                            <ValidationProvider name="applyProgramLevelUndergrad" rules="required" v-slot="{valid, errors}">
                                <b-form-group :label="$t('program_level')">
                                    <parameter-selectbox code="program_levels" :without-items="'H'" :public="true" v-model="formData.target_program_level" :validateError="errors[0]" ></parameter-selectbox>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                    </template>
                    <template>
                        <template v-if="!['D','YL'].includes(formData.target_program_level)">
                        <b-col xs="12" sm="6" class="mb-3" key="ug-faculty">
                            <ValidationProvider name="applyFaculty" rules="required" v-slot="{errors}">
                                <b-form-group :label="$t('faculty')">
                                    <faculty-selectbox v-model="formData.bau_faculty_code" :level="formData.target_program_level==='YL'?'LU':formData.target_program_level" :code="formData.target_program_level==='YL'?'34':null" :validateError="errors[0]" :public="true"></faculty-selectbox>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        <b-col xs="12" sm="6" class="mb-3" key="ug-department">
                            <ValidationProvider name="applyDepartment" rules="required" v-slot="{valid, errors}">
                                <b-form-group :label="$t('department')">
                                    <department-selectbox
                                        v-model="formData.bau_department_code"
                                        :faculty_code="formData.bau_faculty_code"
                                        :validateError="errors[0]"
                                        :public="true"
                                    ></department-selectbox>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                        </template>
                        <b-col xs="12" sm="4" class="mb-3" key="grad-thesis" v-if="['YL'].includes(formData.target_program_level)">
                            <ValidationProvider name="thesisType" rules="required" v-slot="{valid, errors}">
                                <b-form-group :label="$t('with_without_thesis')">
                                    <ThesisWithTrueFalseSelectbox :returnStringValue="true" v-model="formData.graduate_thesis_type" :validateError="errors[0]" />
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>

                        <b-col cols="12" class="mb-3" key="ug-program">
                            <ValidationProvider name="applyProgram" rules="required" v-slot="{valid, errors}">
                                <b-form-group :label="$t('program')">
                                    <program-selectbox
                                        @change="selectionProgram"
                                        :with_thesis="formData.graduate_thesis_type==='with_thesis'"
                                        :level="formData.target_program_level==='D'?'D':''"
                                        v-model="formData.bau_program_code"
                                        :faculty_code="formData.target_program_level==='D'?'34':formData.bau_faculty_code"
                                        :department_code="formData.bau_department_code"
                                        :validateError="errors[0]"
                                        :public="true"
                                    ></program-selectbox>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>

                    </template>
                </b-row>
            </fieldset>

            <div class="d-flex justify-content-center mt-3">
                <b-button variant="primary" @click="submitAndNext" :disabled="loading" class="px-5 font-weight-bold shadow-sm">
                    <b-spinner small v-if="loading"></b-spinner>
                    <span v-else>{{ $t('next') }}</span>
                </b-button>
            </div>
        </ValidationObserver>
    </div>
</template>
<script>
import {ValidationObserver, ValidationProvider} from "vee-validate";
import ParameterSelectbox from "@/components/interactive-fields/ParameterSelectbox.vue";
import FacultySelectbox from "@/components/interactive-fields/FacultySelectbox.vue";
import DepartmentSelectbox from "@/components/interactive-fields/DepartmentSelectbox.vue";
import ProgramSelectbox from "@/components/interactive-fields/ProgramSelectbox.vue";
import ThesisWithTrueFalseSelectbox from "@/components/interactive-fields/ThesisWithTrueFalseSelectbox.vue";
import SpecialStudentApplicationRegisterServices from "@/services/SpecialStudentApplicationRegisterServices";
import YoksisService from "@/services/YoksisService";
export default {
    name: "ProgramSelectionTab",
    components: {
        ValidationObserver,
        ValidationProvider,
        ParameterSelectbox,
        FacultySelectbox,
        DepartmentSelectbox,
        ProgramSelectbox,
        ThesisWithTrueFalseSelectbox
    },
    props: {
        initialData: {
            type: Object,
            default: () => ({})
        },
        countDown:{
            type: Number,
            default: 0
        }
    },
    mounted() {
      this.$nextTick(()=>{
          this.formData=this.getWithExpiry('specialStudentData',true)
          this.setCourseInformation()
      })

    },
    data() {
        return {
            loading: false,
            fetchingYoksis: false,
            selectedEducation: null,
            yoksisResults: [],
            formData:{},
            yoksisFields: [
                { key: 'university_name', label: this.$t('university')},
                { key: 'unit_type', label: this.$t('graduation_degree') },
                { key: 'faculty', label: this.$t('faculty') },
                { key: 'program', label: this.$t('department') },
                { key: 'leaving_date', label:this.$t('graduation_year') },
                { key: 'diploma_point', label: this.$t('graduation_average') },
                { key: 'actions', label: '' },
            ],
            loadingSections: false,
        }
    },
    watch:{
        'formData.target_program_level'(newVal, oldVal){
            if (oldVal !== undefined && newVal !==oldVal) {
                this.formData.bau_program_code=null
                this.formData.bau_faculty_code=null
                this.formData.bau_department_code=null
                this.formData.graduate_thesis_type=null
            }
        }
    },
    methods: {
        setCourseInformation (){
            const setCourseInformation= {
                university_name: this.formData.home_university_name??null,
                unit_type: this.formData.home_program_level ??null,
                faculty: this.formData.home_faculty_name ??null,
                program: this.formData.home_program_name,
                leaving_date: this.formData.education_semester??null,
                diploma_point: this.formData.education_class ??null
            };
            if (setCourseInformation.university_name && setCourseInformation.program && setCourseInformation.unit_type){
                this.yoksisResults.push(setCourseInformation)
                this.selectedEducation=setCourseInformation
            }


        },
        async fetchYoksisData() {
            this.fetchingYoksis = true;
            try {
                const response = await YoksisService.educationInformation(this.formData.national_id,this.formData.document_barcode_no);
                 const data =response.data.data
                  const filter= data.filter((item)=>{
                     const find =this.yoksisResults.find(result=>result.university_name===item.university_name)
                     if (find){
                         return
                     }
                     return item
                 })
                this.yoksisResults.push(...filter);
                 this.$nextTick(()=>{
                     this.formData.document_barcode_no = null
                     this.$refs.programForm.reset({ document_barcode_no: null });
                 })
            } catch(error) {
                this.showErrors(error, null);
            } finally {
                this.fetchingYoksis = false;
            }
        },
        selectEducation(item) {
            this.selectedEducation = item;
            if (item.personal_information){
                this.formData.home_university_name = item.university_name;
                this.formData.home_faculty_name = item.faculty;
                this.formData.home_program_name = item.program;
                this.formData.home_program_level = item.unit_type;
                this.formData.education_semester = item.leaving_date;
                this.formData.education_class = item.class;
            }
            this.formData.education_not_listed = false;
            this.$toast.success(this.$t('proccess_success'))
        },
        addManualEducation() {
            if (!this.formData.home_university_name || !this.formData.home_program_name) {
                return;
            }
           const newRecord ={
                university_name: this.formData.home_university_name,
                unit_type: this.formData.home_program_level || '-',
                faculty: this.formData.home_faculty_name || '-',
                program: this.formData.home_program_name,
                leaving_date: this.formData.education_semester || '-',
                diploma_point: this.formData.education_class || '-'
            };
            if (this.yoksisResults && this.yoksisResults.length > 0 && newRecord) {
                const isSelected = this.yoksisResults.some((result)=> result.university_name===newRecord.university_name && result.unit_type===newRecord.unit_type && result.faculty===newRecord.faculty && result.program===newRecord.program)
                if (isSelected){
                    this.$toast.error(this.$t('already_available'));
                     return;
                }
            }
            this.yoksisResults.push(newRecord);
            this.selectEducation(newRecord);
        },

        async submitAndNext() {
            const isValid = await this.$refs.programForm.validate();
            if (!isValid) {
                return;
            }
            if(!this.formData.education_not_listed && !this.selectedEducation) {
                this.$toast.error(this.$t('education_and_exam_reqired'));
                return;
            }

            this.loading = true;
            let payload = {
                national_id: this.formData.national_id,
                passport_number: this.formData.passport_number,
                pin: this.formData.pin,

                registration_type: this.formData.registration_type,

                education_semester: this.formData.education_semester,
                education_class: this.formData.education_class ,

                home_university_name: this.formData.home_university_name,
                home_faculty_name: this.formData.home_faculty_name,
                home_program_name: this.formData.home_program_name,
                home_program_level: this.formData.home_program_level ,
                education_not_listed: this.formData.education_not_listed?1:0,
                bau_faculty_code: this.formData.bau_faculty_code,
                bau_department_code: this.formData.bau_department_code,
                bau_program_code: this.formData.bau_program_code,
                target_program_level: this.formData.target_program_level ,
                graduate_thesis_type: this.formData.graduate_thesis_type !== null ? (this.formData.graduate_thesis_type ? 'with_thesis' : 'without_thesis') : null,
            };

            if (payload.national_id){
                delete payload.passport_number;
            }
            if (payload.passport_number) {
                delete payload.national_id;
            }
            SpecialStudentApplicationRegisterServices.updateProgramSelection(payload).then(response => {
                this.setWithExpiry('specialStudentData', response.data.data, this.countDown,true);
                this.$toast.success(this.$t("proccess_success"));
                this.$emit('next',2);
            }).catch(e => {
                this.showErrors(e, null);
            }).finally(()=>{
                this.loading = false;
            })
        },
        selectionProgram(program){
            if (this.formData.target_program_level==='D' && program!==null){
                this.formData.bau_faculty_code = program.faculty_code;
                this.formData.bau_department_code=program.department_code
            }
        }
    }
}
</script>
